// apps/admin/src/features/monitoring/WebhookPayloadModal.tsx
//
// Phase 8.3 Wave 7 Task 54 — Webhook payload viewer modal.
//
// vg-binding: CONTEXT:decisions=P8.D-129,P8.D-140
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Per CONTEXT.md (Webhook Logs monitoring) +
// (4-axis signature validation: signature_ok / timestamp_ok / ip_ok /
// idempotency_ok). Consumes GET /api/v1/admin/monitoring/webhook-logs/:id/payload.
//
// Surface:
//   - Validation block (per-check booleans badge row)
//   - Request headers (table key/value, monospace, scrollable)
//   - Request body (JSON pretty-printed when parseable, raw otherwise;
//     collapse / expand toggle for very large payloads)
//   - Response status + body
//
// Error envelope handling — INTERFACE-STANDARDS §error-shape priority:
//   user_message > message > network_fallback. ApiError already
//   resolves this on construction; we just surface .message.

import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';

import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  cn,
} from '@printway/ui';

import { apiClient, ApiError } from '../../lib/apiClient.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Wire shape — mirrors API-CONTRACTS GET .../webhook-logs/:id/payload.
// ---------------------------------------------------------------------------

export interface WebhookPayloadValidation {
  signature_ok: boolean;
  timestamp_ok: boolean;
  ip_ok: boolean;
  idempotency_ok: boolean;
}

export interface WebhookPayloadDetail {
  id: string;
  request_headers: Record<string, string>;
  request_body: string;
  response_body: string | null;
  response_status: number | null;
  validation: WebhookPayloadValidation;
}

export interface WebhookPayloadModalProps {
  open: boolean;
  webhookId: string | null;
  onOpenChange: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function tryPrettyJson(raw: string): { pretty: string; isJson: boolean } {
  if (!raw) return { pretty: '', isJson: false };
  try {
    const parsed = JSON.parse(raw);
    return { pretty: JSON.stringify(parsed, null, 2), isJson: true };
  } catch {
    return { pretty: raw, isJson: false };
  }
}

const VALIDATION_LABELS: Array<{
  key: keyof WebhookPayloadValidation;
  labelKey: string;
}> = [
  { key: 'signature_ok', labelKey: 'monitoring.webhookLogs.payload.fullLabels.signature' },
  { key: 'timestamp_ok', labelKey: 'monitoring.webhookLogs.payload.fullLabels.timestamp' },
  { key: 'ip_ok', labelKey: 'monitoring.webhookLogs.payload.fullLabels.ip' },
  { key: 'idempotency_ok', labelKey: 'monitoring.webhookLogs.payload.fullLabels.idempotency' },
];

function ValidationBadge({
  label,
  ok,
}: {
  label: string;
  ok: boolean;
}): JSX.Element {
  const { t } = useTranslation('p83');
  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-md border px-3 py-2 text-xs',
        ok
          ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-900'
          : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 text-red-900',
      )}
      data-testid={`webhook-validation-${ok ? 'pass' : 'fail'}`}
    >
      <span>{label}</span>
      <span className="font-semibold">
        {ok
          ? t('monitoring.webhookLogs.validation.pass')
          : t('monitoring.webhookLogs.validation.fail')}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WebhookPayloadModal({
  open,
  webhookId,
  onOpenChange,
}: WebhookPayloadModalProps): JSX.Element | null {
  const { t } = useTranslation('p83');
  const [detail, setDetail] = useState<WebhookPayloadDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [collapseBody, setCollapseBody] = useState(false);
  const [collapseResponse, setCollapseResponse] = useState(false);

  useEffect(() => {
    if (!open || !webhookId) {
      setDetail(null);
      setErrorMsg(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErrorMsg(null);
    void (async () => {
      try {
        const data = await apiClient.get<WebhookPayloadDetail>(
          `/api/v1/admin/monitoring/webhook-logs/${encodeURIComponent(webhookId)}/payload`,
        );
        if (!cancelled) {
          setDetail(data);
          // Auto-collapse large bodies (>4KB) for performance.
          setCollapseBody((data.request_body?.length ?? 0) > 4096);
          setCollapseResponse((data.response_body?.length ?? 0) > 4096);
        }
      } catch (err) {
        if (cancelled) return;
        // INTERFACE-STANDARDS error-shape: ApiError already resolves
        // user_message > message; fall back to a safe default.
        const msg =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : t('monitoring.webhookLogs.payload.loadError');
        setErrorMsg(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch keyed on open/webhookId only; `t` (locale-keyed identity) is just the error-text fallback and must not trigger a refetch on language switch
  }, [open, webhookId]);

  const requestPretty = useMemo(
    () => tryPrettyJson(detail?.request_body ?? ''),
    [detail?.request_body],
  );
  const responsePretty = useMemo(
    () => tryPrettyJson(detail?.response_body ?? ''),
    [detail?.response_body],
  );

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      className="max-w-3xl"
      aria-label={t('monitoring.webhookLogs.payload.viewerAria')}
    >
      <DialogHeader>
        <DialogTitle data-testid="webhook-payload-title">
          {t('monitoring.webhookLogs.payload.title')}
        </DialogTitle>
        <p className="mt-1 text-xs text-pw-text-muted" data-testid="webhook-payload-id">
          {webhookId ?? '—'}
        </p>
      </DialogHeader>

      <div
        className="mt-4 max-h-[60vh] space-y-4 overflow-y-auto pr-1"
        data-testid="webhook-payload-body"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : null}

        {!loading && errorMsg ? (
          <div
            className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-900"
            role="alert"
            data-testid="webhook-payload-error"
          >
            {errorMsg}
          </div>
        ) : null}

        {!loading && detail ? (
          <>
            {/* Validation block */}
            <section data-testid="webhook-validation-block">
              <h3 className="mb-2 text-sm font-semibold text-pw-text">
                {t('monitoring.webhookLogs.payload.validationTitle')}
              </h3>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {VALIDATION_LABELS.map((v) => (
                  <ValidationBadge
                    key={v.key}
                    label={t(v.labelKey)}
                    ok={detail.validation[v.key]}
                  />
                ))}
              </div>
            </section>

            {/* Request headers */}
            <section data-testid="webhook-headers-block">
              <h3 className="mb-2 text-sm font-semibold text-pw-text">
                {t('monitoring.webhookLogs.payload.requestHeaders')}
              </h3>
              <div className="overflow-x-auto rounded-md border border-pw-border bg-pw-bg-muted/30">
                <table className="w-full text-xs">
                  <tbody>
                    {Object.entries(detail.request_headers ?? {}).map(
                      ([k, v]) => (
                        <tr
                          key={k}
                          className="border-b border-pw-border last:border-b-0"
                        >
                          <td className="w-1/3 px-3 py-1.5 font-mono text-pw-text-muted">
                            {k}
                          </td>
                          <td className="break-all px-3 py-1.5 font-mono">
                            {v}
                          </td>
                        </tr>
                      ),
                    )}
                    {Object.keys(detail.request_headers ?? {}).length === 0 ? (
                      <tr>
                        <td
                          colSpan={2}
                          className="px-3 py-2 text-center text-pw-text-muted"
                        >
                          {t('monitoring.webhookLogs.payload.noHeaders')}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Request body */}
            <section data-testid="webhook-request-body-block">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-pw-text">
                  {t('monitoring.webhookLogs.payload.requestBody')}
                  {requestPretty.isJson ? (
                    <span className="ml-2 rounded bg-pw-bg-muted px-1.5 py-0.5 text-xs text-pw-text-muted">
                      JSON
                    </span>
                  ) : null}
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setCollapseBody((c) => !c)}
                  data-testid="webhook-toggle-request-body"
                >
                  {collapseBody
                    ? t('monitoring.webhookLogs.payload.expand')
                    : t('monitoring.webhookLogs.payload.collapse')}
                </Button>
              </div>
              <pre
                className={cn(
                  'overflow-x-auto rounded-md border border-pw-border bg-pw-bg-muted/30 p-3 font-mono text-xs',
                  collapseBody ? 'max-h-24' : 'max-h-[300px]',
                  'overflow-y-auto',
                )}
                data-testid="webhook-request-body"
              >
                {requestPretty.pretty || t('monitoring.webhookLogs.payload.empty')}
              </pre>
            </section>

            {/* Response */}
            <section data-testid="webhook-response-block">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-pw-text">
                  {t('monitoring.webhookLogs.payload.response')}
                  {detail.response_status !== null ? (
                    <span
                      className={cn(
                        'ml-2 rounded px-1.5 py-0.5 text-xs',
                        detail.response_status >= 200 &&
                          detail.response_status < 300
                          ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-900'
                          : 'bg-red-100 dark:bg-red-950/40 text-red-900',
                      )}
                    >
                      HTTP {detail.response_status}
                    </span>
                  ) : (
                    <span className="ml-2 rounded bg-pw-bg-muted px-1.5 py-0.5 text-xs text-pw-text-muted">
                      {t('monitoring.webhookLogs.payload.noResponse')}
                    </span>
                  )}
                </h3>
                {detail.response_body ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setCollapseResponse((c) => !c)}
                    data-testid="webhook-toggle-response-body"
                  >
                    {collapseResponse
                      ? t('monitoring.webhookLogs.payload.expand')
                      : t('monitoring.webhookLogs.payload.collapse')}
                  </Button>
                ) : null}
              </div>
              {detail.response_body ? (
                <pre
                  className={cn(
                    'overflow-x-auto rounded-md border border-pw-border bg-pw-bg-muted/30 p-3 font-mono text-xs',
                    collapseResponse ? 'max-h-24' : 'max-h-[300px]',
                    'overflow-y-auto',
                  )}
                  data-testid="webhook-response-body"
                >
                  {responsePretty.pretty}
                </pre>
              ) : (
                <p className="text-xs text-pw-text-muted">
                  {t('monitoring.webhookLogs.payload.noResponseBody')}
                </p>
              )}
            </section>
          </>
        ) : null}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          {t('monitoring.webhookLogs.payload.close')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export default WebhookPayloadModal;
